import React from 'react';
import { Box, Group, Text, Badge } from '@mantine/core';
import type { SlopPostDto } from './types';

export interface SlopVideoCardProps {
  post: Pick<
    SlopPostDto,
    | 'id'
    | 'title'
    | 'username'
    | 'thumbnailUrl'
    | 'isEmbedded'
    | 'likeCount'
    | 'commentCount'
    | 'isLikedByCurrentUser'
    | 'linkedCaption'
    | 'fileSizeMb'
    | 'createdAt'
  >;
  thumbnailUrl?: string | null;
  formatDate?: (iso: string) => string;
  onOpen?: () => void;
  actionBar?: React.ReactNode;
}

const defaultFormat = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

/**
 * Presentational Slop library grid card. Host wires API, votes, and routes.
 */
export function SlopVideoCard ({
  post,
  thumbnailUrl,
  formatDate = defaultFormat,
  onOpen,
  actionBar
}: SlopVideoCardProps) {
  const thumb = thumbnailUrl ?? post.thumbnailUrl;

  return (
    <Box
      onClick={onOpen}
      sx={{
        cursor: onOpen ? 'pointer' : undefined,
        background: 'var(--gs-surface2)',
        border: '1px solid var(--gs-border)',
        borderRadius: 8,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': onOpen
          ? { borderColor: 'var(--gs-accent)', transform: 'translateY(-2px)' }
          : undefined
      }}
    >
      <Box
        sx={{
          background: 'var(--gs-surface1)',
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        {post.isEmbedded ? (
          <Text sx={{ fontSize: 12, color: 'var(--gs-muted)' }}>Embedded Video</Text>
        ) : thumb ? (
          <Box
            component="img"
            src={thumb}
            alt={post.title ?? ''}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Text sx={{ fontSize: 12, color: 'var(--gs-muted)' }}>No Thumbnail</Text>
        )}
      </Box>

      <Box p="md">
        <Text sx={{ fontSize: 14, fontWeight: 600, color: 'var(--gs-text)' }}>{post.title}</Text>
        <Text sx={{ fontSize: 12, color: 'var(--gs-muted)', marginTop: 4 }}>by {post.username}</Text>

        {post.linkedCaption && (
          <Text
            sx={{
              fontSize: 11,
              color: 'var(--gs-accent)',
              fontFamily: 'DM Mono, monospace',
              background: 'rgba(232,93,4,0.08)',
              padding: '3px 8px',
              borderRadius: 4,
              marginTop: 8,
              display: 'inline-block'
            }}
          >
            {post.linkedCaption}
          </Text>
        )}

        {actionBar && <Box mt="sm">{actionBar}</Box>}

        <Group position="apart" mt="xs">
          <Text sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>{formatDate(post.createdAt)}</Text>
          {post.fileSizeMb != null && (
            <Badge size="sm" variant="light" sx={{ fontFamily: 'DM Mono, monospace', fontSize: 10 }}>
              {post.fileSizeMb} MB
            </Badge>
          )}
        </Group>
      </Box>
    </Box>
  );
}
